// Central tracking helper. Every conversion event on the site flows through
// `track()` so the same parameter shape lands in PostHog, Vercel Analytics,
// and GA4 (G-Q063QBRZT0). PostHog also supplies autocapture and session replay.
//
// Why centralize: GA4 was showing 0 key events because each surface fired its
// own one-off snippet (or fired nothing at all — WaitlistModal + DetoxChallengeSignup
// were silent on submit). Routing every event through this helper means:
//   1. Naming stays consistent across surfaces.
//   2. Parameter shape stays consistent (so GA4 Explore reports actually work).
//   3. Adding a third sink later (Posthog, Amplitude) is a one-file change.
//
// All functions are safe to call during SSR and share one explicit-consent
// boundary. No provider is initialized or sent an event before opt-in.
//
// 2026-06-29: renamed the per-CTA surface identifier from `source` to
// `cta_surface` to stop GA4 from interpreting our custom event-scoped
// parameter as the built-in UTM `source` field (which was leaking our
// CTA labels like `blog_hero_cta_food` into the Session Source report).
// See `cta_surface` field below for full context. GA4 admin needs a new
// custom dimension "CTA Surface" with parameter `cta_surface` (event-scoped).

import { track as vercelTrack } from '@vercel/analytics'
import { capturePostHog } from '@/utils/posthogClient'
import { getBrowserAnalyticsConsent } from '@/utils/analyticsConsent'
import { runAnalyticsSinksIfConsented } from '@/utils/analyticsGate'
import { isSensitiveAnalyticsUrl } from '@/utils/analyticsUrl'
import { classifyAppStorePlatform } from '@/utils/appStoreTracking'
import { sanitizeAnalyticsProperties } from '@/utils/analyticsProperties'
import {
  sendGoogleAnalyticsEvent,
  type GoogleAnalyticsTarget,
} from '@/utils/googleAnalytics'

/**
 * Canonical event names used across the site. Keeping these as a string union
 * means TypeScript will catch typos at the call site — important because GA4
 * silently accepts misspellings and drops them into a noise bucket.
 *
 * Events marked KEY are the ones that should be flagged as "key events" in
 * GA4 (Admin → Events → toggle "Mark as key event"). These are the conversion
 * signals the site is being measured on.
 */
export type EventName =
  // Reader support: outbound intent only, never a completed payment.
  | 'support_cta_impression'
  | 'support_cta_click'
  | 'site_link_click'           // navigation, public content and footer links
  | 'site_button_click'         // authored action IDs, never button/input text
  // ── Primary conversion events (mark as KEY in GA4) ──────────────────────
  | 'app_store_click'           // KEY — real App Store outbound only; never Android redirects
  | 'android_waitlist_submit'   // KEY — successful waitlist form submit
  | 'email_capture_submit'      // KEY — newsletter / detox / lead form submit
  | 'risk_checker_complete'     // KEY — quiz tool completion
  | 'ingredient_checker_first_result' // KEY — first ingredient result, once per session
  // Certification programme (2026-07-30). This is a B2B revenue line, so a
  // completed application is the highest-value single event on the site by a
  // wide margin — one licensed brand is worth more than a lot of consumer
  // subscriptions. Mark as KEY in GA4.
  | 'certification_application_submit' // KEY — brand completed the application

  // ── Engagement / funnel-shape events (NOT key, but useful in Explore) ───
  // Surface clicks also emit the canonical `app_store_click`. Keeping these
  // as non-key events prevents a single tap from counting as two conversions.
  | 'sticky_app_cta_click'
  | 'hero_answer_click'
  | 'app_store_outbound'        // real outbound, split from Android waitlist routing
  | 'ios_app_store_outbound'    // iOS-device subset of real App Store outbounds
  | 'android_waitlist_redirect' // Android CTA rerouted to the on-site waitlist
  | 'cta_impression'            // every AppStoreLink, exact surface + position denominator
  // This fires once per scan. The once-per-session first-result sibling above
  // is the conversion signal; this event remains useful as scan-volume data.
  | 'ingredient_checker_complete'
  | 'hero_answer_impression'    // above-the-fold answer card rendered
  | 'certification_application_start' // brand touched the first field
  | 'certification_application_error' // submit failed (validation / duplicate / server)

  // ── Per-surface impression events ──────────────────────────────────────
  // 2026-07-14: these three used to fire `hero_app_cta_impression`, the SAME
  // event name the blog hero ribbon used. Four unrelated components were
  // therefore pooling into one impression counter, which made the hero
  // ribbon's CTR uncomputable — its 2,975 "impressions" were really the sum of
  // four surfaces, while only the ribbon itself fired the matching click and
  // dismiss events. Any CTR derived from that pair was meaningless. Each
  // surface now owns its own impression event so per-CTA CTR is real.
  | 'first_scan_callout_impression'
  | 'ingredient_checker_promo_impression'
  | 'scan_demo_impression'
  // Added 2026-07-30. MidArticleCTA fired a click but never an impression, so
  // the one CTA that sits in the reader's actual scroll range had no
  // denominator and its CTR could not be computed at all. Fires on first
  // intersection, not on mount, so "impression" means genuinely scrolled into
  // view rather than merely rendered somewhere down the page.
  | 'mid_article_cta_impression'

  // ── Retired 2026-07-14 (HeroAppCTA removed; see HeroAnswer) ────────────
  // Kept in the union so historical GA4 data stays interpretable and any
  // straggler call site still typechecks. Nothing in the app fires these.
  | 'hero_app_cta_click'
  | 'hero_app_cta_impression'
  | 'hero_app_cta_dismiss'
  | 'android_waitlist_open'     // user opened the modal but hasn't submitted
  | 'android_waitlist_error'    // submission failed
  | 'android_waitlist_unknown'  // neither delivery sink confirmed before deadline
  | 'campaign_popup_view'       // campaign popup shown (event_id + trigger: scroll|dwell)
  | 'campaign_popup_dismiss'    // user closed a campaign popup (event_id + reason)
  | 'campaign_popup_cta'        // user clicked a link-type campaign CTA
  | 'email_capture_error'       // submission failed
  | 'email_capture_unknown'     // delivery may still complete after the deadline
  | 'email_capture_dismiss'     // visitor closed an email offer without submitting
  // Newsletter (August 2026). The impression event is what makes the scroll
  // prompt's conversion rate computable — without it you only ever see
  // submits and cannot tell a good offer from a heavily-shown one.
  | 'email_capture_impression'
  | 'risk_checker_start'        // user answered the first question
  | 'ingredient_checker_start'  // user pasted / began typing the first time
  | 'scan_started'              // every explicit scan button activation
  | 'scan_success'              // scan calculation completed and results rendered
  | 'validation_error'          // client-side scanner validation stopped a scan
  | 'scan_failed'               // unexpected scanner failure
  // Once-per-session sibling of ingredient_checker_complete, added 2026-08-03.
  // `_complete` fires on EVERY scan while `_start` fires once, so the two were
  // never a valid funnel (89 starts vs 284 completes in the 28 days to 08-02 —
  // different units, not a double-fire). This fires only on the first scan of a
  // session so that start -> first_result -> app_store_click is a real funnel.
  // Inline in-article engagement widget (see InlineGuessReveal).
  | 'guess_reveal_view'         // widget scrolled into view
  | 'guess_reveal_answer'       // reader committed to a guess
  | 'guess_reveal_result'       // reader saw the reveal (right/wrong + delta)
  | 'scroll_25_article'
  | 'scroll_50_article'
  | 'scroll_75_article'
  | 'scroll_90_article'
  | 'related_article_click'
  | 'topic_hub_click'
  | 'faq_expand'
  | 'pricing_card_view'
  | 'pricing_cta_click'
  | 'preferred_source_badge_impression'
  | 'preferred_source_badge_click'
  // Product database explorer (/products). `product_search_miss` is the
  // valuable one: it is a live queue of the products people want scored and
  // we do not have yet, which is what decides what gets added next.
  | 'product_search'
  | 'product_search_miss'
  | 'product_result_click'

/**
 * Parameter shape used across every event. All fields are optional — fill in
 * the ones that apply at the call site. GA4 will surface them as event
 * dimensions in Explore; Vercel will surface them in the custom-events panel.
 *
 * `cta_surface` is the single most important parameter — it labels WHICH
 * surface the event fired from (e.g. `home_hero`, `blog_inline_after_intro`,
 * `food_hub_sticky_mobile`) so you can break down conversion per CTA position.
 *
 * NOTE — why not `source`: GA4 has a built-in `source` UTM attribution
 * field. If we sent our CTA label under the key `source`, GA4 would
 * interpret it as the session's traffic source and pollute the Session
 * Source report with values like `blog_hero_cta_food`. Using `cta_surface`
 * avoids the collision.
 */
export interface EventParams {
  /** Stable snake_case identifier for the CTA surface (e.g. `home_hero`). */
  cta_surface?: string
  /** Path of the page the event fired from. Auto-filled if omitted. */
  page_path?: string
  /** One of `home | blog | hub | scanner | guide | faq | static`. */
  page_type?: string
  /** Blog slug, when applicable. */
  post_slug?: string
  /** Blog topic (water / food / health / etc), when applicable. */
  post_topic?: string
  /** Where on the page the CTA sits (`above_fold | mid_article | bottom | sticky`). */
  cta_position?: string
  /** Outbound URL, when applicable. */
  destination?: string
  /** A/B test variant label, when running experiments. */
  experiment_variant?: string
  /** Free-form extension. */
  [key: string]: string | number | boolean | undefined
}

/**
 * Fire an event to both Vercel Analytics and GA4. Safe in SSR.
 *
 * - On Vercel Hobby plan custom events are no-ops; they start recording the
 *   instant the project upgrades to Pro. No code changes needed at that point.
 * - GA4 receives the event with `cta_surface`, `page_path`, and any extra
 *   params. Configure these as custom dimensions in GA4 (Admin → Custom
 *   definitions) so they appear as breakdown options in Explore.
 */
export function track(name: EventName, params: EventParams = {}): void {
  if (typeof window === 'undefined') return
  if (isSensitiveAnalyticsUrl(window.location.href)) return
  const consent = getBrowserAnalyticsConsent()

  // Backward-compat shim: a lot of existing call sites pass `source: '...'`
  // (from before we discovered the GA4 collision with the built-in UTM
  // `source` field). Transparently rewrite to `cta_surface` so old call
  // sites keep working and GA4 stops attributing CTA labels as session
  // sources. This single line replaces a 25-call-site refactor across
  // 9 components — call sites can migrate to `cta_surface` at their own
  // pace; until then the remap handles them. The rewrite is safe because
  // `source` is no longer in the EventParams typed interface, so any
  // call site passing it is using the index-signature escape hatch.
  const incoming = { ...params } as Record<string, unknown>
  const remappedSource =
    typeof incoming.source === 'string' && !incoming.cta_surface
      ? { ...incoming, cta_surface: incoming.source as string }
      : incoming
  if ('source' in remappedSource) {
    delete (remappedSource as Record<string, unknown>).source
  }

  // Auto-fill page_path from the browser if the caller didn't supply one.
  // We don't auto-fill in SSR because there's no window to read from.
  const enriched = sanitizeAnalyticsProperties(!remappedSource.page_path
    ? { ...remappedSource, page_path: window.location.pathname }
    : remappedSource)

  runAnalyticsSinksIfConsented(consent, [
    // PostHog also rechecks consent around its asynchronous SDK load, covering
    // a withdrawal while the dynamic import is in flight.
    () => capturePostHog(name, enriched),
    () =>
      vercelTrack(
        name,
        enriched as Record<string, string | number | boolean | null>
      ),
    () =>
      sendGoogleAnalyticsEvent(
        window as unknown as GoogleAnalyticsTarget,
        name,
        enriched
      ),
  ])
}

/**
 * Convenience wrapper for the App Store click — same as `track('app_store_click', ...)`
 * but enforces that `cta_surface` is always set, since it's the field every
 * dashboard breakdown needs.
 */
export function trackAppStoreClick(ctaSurface: string, extra: EventParams = {}): void {
  const outboundPlatform =
    typeof navigator === 'undefined'
      ? 'other'
      : classifyAppStorePlatform(navigator.userAgent)
  if (outboundPlatform === 'android') {
    trackAndroidWaitlistRedirect(ctaSurface, extra)
    return
  }
  const params = {
    cta_surface: ctaSurface,
    destination: 'app_store',
    destination_type: 'ios_app_store',
    outbound_platform: outboundPlatform,
    ...extra,
  }

  // Keep the historical key event, but make its meaning precise: it is now
  // always a real outbound navigation. Android waitlist routing has its own
  // event and can no longer inflate this conversion series.
  track('app_store_click', params)
  track('app_store_outbound', params)
  if (outboundPlatform === 'ios') track('ios_app_store_outbound', params)
}

export function trackAndroidWaitlistRedirect(
  ctaSurface: string,
  extra: EventParams = {},
): void {
  track('android_waitlist_redirect', {
    cta_surface: ctaSurface,
    destination: '/#android-waitlist',
    destination_type: 'android_waitlist',
    outbound_platform: 'android',
    ...extra,
  })
}

export function trackCtaImpression(ctaSurface: string, extra: EventParams = {}): void {
  track('cta_impression', {
    cta_surface: ctaSurface,
    destination: 'app_store',
    destination_type: 'ios_app_store',
    ...extra,
  })
}
